import './static-map.scss';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

class StaticMap {
    constructor (element) {
        this.$staticMap = element;
        this.$map = this.$staticMap.querySelector('[data-static-map="map"]');
        this.mapID = this.$map?.id;
        this.map = null;
        this.marker = [];
        this.shipIcon = this.$staticMap.dataset.shipIcon;
        this.$map = this.$staticMap.querySelector(`#${this.mapID}`);
        this.$activePopup = null;
        this.$activeMarker = null;
        this.$$popups = this.$staticMap.querySelectorAll('[data-ship-id]');
        this.worldGeoJsonUrl = this.$staticMap.dataset.staticMapJson;
        this.boundsArr = [];
        this.language = document.documentElement.lang;
        this.$lastActiveElement = null;
    }

    initialize () {
        this.initMap(() => {
            this.$map?.removeAttribute('tabindex');
            this.setEvents();
        });
    }

    initMap (callback) {
        const bounds = L.latLngBounds(
            L.latLng(-85, -180),
            L.latLng(85, 360)
        );

        this.map = L.map(this.mapID, {
            closePopupOnClick: false,
            dragging: true,
            style: 'lightmode simplified',
            zoomSnap: 0.7,
            maxZoom: 4,
            minZoom: 2,
            trackResize: false,
            scrollWheelZoom: false
        }).setView([51.505, -0.09], 2);

        this.map.setMaxBounds(bounds);

        const tilesURL = (this.language === 'de') ? 'https://{s}.tile.openstreetmap.de/tiles/osmde/{z}/{x}/{y}.png' : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

        L.tileLayer(tilesURL, {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }).addTo(this.map);

        if (typeof callback === 'function') {
            callback();
        }
    }

    setEvents () {
        this.map.whenReady(() => {
            if (staticMapsShips) { // eslint-disable-line
                this.setMarker();
                if (staticMapsShips.length > 1) { // eslint-disable-line
                    this.map.fitBounds(this.boundsArr);
                } else {
                    if (staticMapsShips.length > 0) { // eslint-disable-line
                        const lat = staticMapsShips[0]?.position.lat; // eslint-disable-line
                        const lng = staticMapsShips[0]?.position.lng; // eslint-disable-line

                        if (lat && lng) {
                            this.map.zoomIn(3.5);

                            setTimeout(() => {
                                this.map.setView({lat: lat, lng: lng}); // eslint-disable-line
                            }, 1000);
                        }
                    }
                }
            }
        });
    }

    setMarker () {
        staticMapsShips.forEach((ship) => { // eslint-disable-line
            const shipIcon = L.divIcon({
                className: 'static-map__marker-icon',
                html: '<svg aria-hidden="true" role="none" width="24" height="24" class="icon icon--is-cruisefinder"><use xlink:href="#icon-cruisefinder"></use></svg><span>' + ship.name + '</span>',
                iconSize: [44, 49],
                iconAnchor: [22, 49],
                keyboard: true
            });

            const shipIconActive = L.divIcon({
                className: 'static-map__marker-icon static-map__marker-icon--active',
                html: '<svg aria-hidden="true" role="none" width="24" height="24" class="icon icon--is-cruisefinder"><use xlink:href="#icon-cruisefinder"></use></svg><span>' + ship.name + '</span>',
                iconSize: [44, 49],
                iconAnchor: [22, 49]
            });

            const marker = L.marker([ship.position.lat, ship.position.lng], {
                icon: shipIcon,
                id: ship.id,
                activeIcon: shipIconActive,
                defaultIcon: shipIcon,
                alt: ship.name + 'position marker',
                riseOnHover: true,
                keyboard: true
            }).addTo(this.map);

            this.boundsArr.push([ship.position.lat, ship.position.lng]);
            this.marker.push(marker);

            this.setMarkerEvents(marker);
        });
    }

    setMarkerEvents (marker) {
        const $popup = this.$staticMap.querySelector('[data-ship-id="' + marker.options.id + '"]');
        const $closeButton = $popup.querySelector('[data-ship-popup="close"]');

        const togglePopup = (marker) => {
            if ($popup !== this.$activePopup) {
                if (this.$activePopup !== null) {
                    this.$activePopup.setAttribute('inert', '');
                    this.$activePopup.classList.add('is--hidden');
                    this.$activeMarker.setIcon(this.$activeMarker.options.defaultIcon);
                    this.$activePopup = null;
                    this.$activeMarker = null;
                    this.$lastActiveElement = null;

                    setTimeout(() => {
                        this.$lastActiveElement = document.activeElement;
                        $popup.removeAttribute('inert');
                        $popup.classList.remove('is--hidden');
                        this.map.panTo(marker.getLatLng());
                        this.$activePopup = $popup;
                        this.$activeMarker = marker;
                        marker.setIcon(marker.options.activeIcon);

                        setTimeout(() => {
                            $closeButton?.focus();
                        }, 200);
                    }, 250);
                } else {
                    this.$lastActiveElement = document.activeElement;
                    $popup.removeAttribute('inert');
                    $popup.classList.remove('is--hidden');
                    this.map.panTo(marker.getLatLng());
                    this.$activePopup = $popup;
                    this.$activeMarker = marker;
                    marker.setIcon(marker.options.activeIcon);

                    setTimeout(() => {
                        $closeButton?.focus();
                    }, 200);
                }
            } else {
                this.$activePopup.setAttribute('inert', '');
                this.$activePopup.classList.add('is--hidden');
                this.$activeMarker.setIcon(this.$activeMarker.options.defaultIcon);
                this.$activePopup = null;
                this.$activeMarker = null;
                this.$lastActiveElement = null;
            }
        };

        marker.on('keydown', (e) => {
            if (e.originalEvent.key === 'Enter') {
                togglePopup(marker);
            }
        });

        marker.on('click', () => {
            togglePopup(marker);
        });

        $closeButton.addEventListener('click', () => {
            $popup.classList.add('is--hidden');
            this.$activePopup.setAttribute('inert', '');
            this.$activeMarker.setIcon(this.$activeMarker.options.defaultIcon);
            this.$activeMarker = null;
            this.$activePopup = null;

            if (this.$lastActiveElement !== null) {
                this.$lastActiveElement.focus();
            }
        });
    }
}

export { StaticMap };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $staticMap = $context.querySelector('[data-static-map="root"]');

        if ($staticMap) {
            const initializeMap = ($placeholder) => {
                const staticMap = new StaticMap($staticMap);
                staticMap.initialize();

                if ($placeholder) {
                    $placeholder.remove();
                }
            };

            const $placeholder = $staticMap.querySelector('.media__restriction');

            const waitForConsent = setInterval(() => {
                if (typeof OneTrust !== 'undefined') { // eslint-disable-line
                    let mapAllowed = OnetrustActiveGroups.indexOf('C0009') !== -1; // eslint-disable-line

                    if (OnetrustActiveGroups.indexOf('C0009') != -1) { // eslint-disable-line
                        initializeMap($placeholder);
                    } else {
                        const $button = $staticMap.querySelector('[data-restriction="remove"]');

                        if ($button) {
                            $button.addEventListener('click', () => {
                                if (typeof OneTrust.UpdateConsent !== 'undefined') { // eslint-disable-line
                                    OneTrust.UpdateConsent("Category","C0009:1"); // eslint-disable-line
                                } else {
                                    OneTrust.ToggleInfoDisplay(); // eslint-disable-line
                                }
                            });
                        }

                        if ($placeholder) {
                            $placeholder.classList.add('is--shown');
                            $placeholder.closest('[data-media="youtube"]')?.classList.add('has--restriction');
                        }
                    }

                    OneTrust.OnConsentChanged(() => { // eslint-disable-line
                        if (mapAllowed != (OnetrustActiveGroups.indexOf('C0009') !== -1)) {  // eslint-disable-line
                            mapAllowed = OnetrustActiveGroups.indexOf('C0009') !== -1; // eslint-disable-line

                            if ($placeholder && mapAllowed) {
                                initializeMap($placeholder);
                            } else {
                                setTimeout(() => {
                                    location.reload();
                                }, 500);
                            }
                        }
                    });

                    clearInterval(waitForConsent);
                }
            }, 300);
        }
    }
});
